// ── Collateral Management — Borrowing Base hub (list + upload) ──
import { useState, useEffect } from "react";
import { B } from "../../shared/theme";
import { fmt, fmtDate } from "../../shared/format";
import { Card, ErrorMsg, SectionHeader, Spinner, Th, ThNum, Td, TdNum, Btn } from "../../shared/ui";
import { useApp } from "../../shared/appContext";
import { checkUploadSize } from "../../shared/api";

const readBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onerror = reject;
  r.onload = () => resolve(String(r.result).split(",")[1]);
  r.readAsDataURL(file);
});

export const CollateralHome = () => {
  const { api, go, currentUser, view } = useApp();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  // upload form state
  // Opens straight into the upload form when arriving from the Portfolio page's
  // "New Borrowing Base" button.
  const [open, setOpen] = useState(() => !!(view && view.newbbc));
  const [acct, setAcct] = useState("");
  const [loanid, setLoanid] = useState("");
  const [acctLoans, setAcctLoans] = useState([]);
  const [allLoans, setAllLoans] = useState([]);   // every BBC-eligible loan, fetched once
  const [file, setFile] = useState(null);
  const [asof, setAsof] = useState("");
  const [boxlink, setBoxlink] = useState("");
  const [busy, setBusy] = useState(false);
  const [uerr, setUerr] = useState(null);
  const [filterAcct, setFilterAcct] = useState("");   // company filter
  // Each company shows its RECENT_LIMIT newest BBCs; older ones are one click
  // away per company. Keyed by company name (the group key below).
  const RECENT_LIMIT = 10;
  const [expanded, setExpanded] = useState({});

  // ── Where the workbook comes from ──
  // "box" asks the server to fetch a file already in the account's Borrowing
  // Base Package folder. "upload" base64s a local file into the request, which
  // the hosting platform refuses over 30 MB — so Box is the default, and the
  // only route that works for a large certificate.
  const [src, setSrc] = useState("box");
  const [boxFiles, setBoxFiles] = useState(null);      // null = not loaded yet
  const [boxFileId, setBoxFileId] = useState("");
  const [boxBusy, setBoxBusy] = useState(false);
  const [boxNote, setBoxNote] = useState(null);        // no folder set, truncated list, fetch failed

  // ── What may carry a borrowing base ──
  // Only a revolving line of credit, ever — confirmed with the business line.
  // Delayed draw term loans were briefly allowed here and are not.
  const bbcEligible = (l) =>
    !["Terminated", "Paid Off", "Funding in Progress"].includes(l.loanstatus) &&
    /^revolving line of credit$/i.test((l.facilitytype || "").trim());

  const load = () => {
    setLoading(true);
    api.get("getBorrowingBases")
      .then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);
  // Accounts and loans are fetched together and filtered by the SAME rule, so
  // the two dropdowns cannot disagree. Listing an account whose only loan the
  // next dropdown then rejects is a dead end the user has to discover by
  // clicking — the account simply does not appear.
  useEffect(() => {
    Promise.all([
      api.get("getAccounts").catch(() => []),
      api.get("getLoans").catch(() => []),      // no accountid = every loan
    ]).then(([accts, loans]) => {
      const eligible = (Array.isArray(loans) ? loans : []).filter(bbcEligible);
      setAllLoans(eligible);
      const withRloc = new Set(eligible.map(l => String(l.accountid)));
      setAccounts((Array.isArray(accts) ? accts : [])
        // funded accounts only (Accrual / Non Accrual) that also hold a
        // revolving line, alphabetical
        .filter(a => ["Accrual", "Non Accrual"].includes(a.accountstatus))
        .filter(a => withRloc.has(String(a.id)))
        .sort((a, b) => (a.companyname || "").localeCompare(b.companyname || "")));
    });
  }, []);

  // Loans for the chosen account, taken from the list already fetched above
  // rather than re-queried — no round trip, and no chance of the two lists
  // being filtered differently.
  //
  // ONE eligible line is auto-selected: it is the only possible answer, and
  // making someone choose from a list of one is a click that teaches nothing.
  // TWO OR MORE stay unselected on purpose — an account with several revolving
  // lines is exactly where a defaulted guess would be wrong silently, and the
  // certificate would be filed against the wrong facility.
  useEffect(() => {
    if (!acct) { setLoanid(""); setAcctLoans([]); return; }
    const mine = allLoans.filter(l => String(l.accountid) === String(acct));
    setAcctLoans(mine);
    setLoanid(mine.length === 1 ? String(mine[0].id) : "");
  }, [acct, allLoans]);

  // ── The account's Box folder, listed on demand ──
  // Fetched when an account is chosen rather than up front: it walks the folder
  // tree in Box, which costs an API call per folder, and doing that for an
  // account nobody selected is work spent on nothing.
  useEffect(() => {
    setBoxFiles(null); setBoxFileId(""); setBoxNote(null);
    if (!acct) return;
    let live = true;                   // a fast second selection must not have
    setBoxBusy(true);                  // the first one's answer land on top of it
    api.get("getBBCBoxFiles", { accountid: acct })
      .then(d => {
        if (!live) return;
        setBoxFiles(Array.isArray(d && d.files) ? d.files : []);
        setBoxNote((d && d.message) || null);
        setBoxBusy(false);
      })
      .catch(e => {
        if (!live) return;
        // Box being unavailable is not a dead end — it means "upload it
        // instead", so say that rather than just showing the error.
        setBoxFiles([]);
        setBoxNote("Couldn't list this account's Box folder (" + e.message + "). Upload the file directly.");
        setBoxBusy(false);
      });
    return () => { live = false; };
  }, [acct]);

  const chosenBoxFile = (boxFiles || []).find(f => String(f.id) === String(boxFileId)) || null;

  const upload = async () => {
    if (!acct || !loanid) { setUerr("Pick a funded account and a loan."); return; }

    const fromBox = src === "box";
    if (fromBox && !chosenBoxFile) { setUerr("Pick the certificate from the account's Box folder."); return; }
    if (!fromBox && !file) { setUerr("Choose a .xlsx file to upload."); return; }

    // Only the upload path has a ceiling. Checked before anything reads the
    // file: the hosting platform refuses an oversized request at its gateway
    // with a bare 413, so the alternative is spending half a minute encoding and
    // pushing a workbook only to be refused by something that never ran the
    // parser. The Box path sends no bytes at all, so it has nothing to check.
    if (!fromBox) {
      const tooBig = checkUploadSize(file);
      if (tooBig) {
        setUerr(tooBig + " This certificate is already in Box — switch the source above to load it from there instead, "
                      + "which sends no file at all and has no size limit.");
        return;
      }
    }

    setBusy(true); setUerr(null);
    try {
      const body = {
        accountid: acct, loanid,
        asofdate: asof || undefined,
        box_url: boxlink.trim() || undefined,
        uploadedby: currentUser?.email || currentUser?.name || null,
      };
      if (fromBox) {
        body.boxfileid = chosenBoxFile.id;
        body.filename = chosenBoxFile.name;
      } else {
        body.filename = file.name;
        body.filedata = await readBase64(file);
      }
      const res = await api.post("saveBorrowingBase", body);
      setOpen(false); setFile(null); setAcct(""); setLoanid(""); setAsof(""); setBoxlink(""); setBoxFileId("");
      go("collateral-bbc", { bbid: res.id });   // jump straight to the new certificate
    } catch (e) { setUerr(e.message); }
    setBusy(false);
  };

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const lbl = { fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4, display: "block" };
  const inp = { width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 13, borderRadius: 6, border: "1px solid #ccc" };

  // ── filter + group by company ──
  const shown = filterAcct ? rows.filter(r => String(r.accountid) === String(filterAcct)) : rows;
  const groupMap = {};
  shown.forEach(r => { const k = r.companyname || String(r.accountid) || "—"; (groupMap[k] = groupMap[k] || []).push(r); });
  // Newest first within each company — sorted here rather than trusting the
  // API's ORDER BY, since the limit below depends on it.
  const byNewest = (a, b) => String(b.asofdate || "").localeCompare(String(a.asofdate || ""))
                          || String(b.uploadedat || "").localeCompare(String(a.uploadedat || ""));
  const groups = Object.keys(groupMap).sort((a, b) => a.localeCompare(b)).map(name => {
    const all = groupMap[name].slice().sort(byNewest);
    const showAll = !!expanded[name];
    return { name, total: all.length, hidden: showAll ? 0 : Math.max(0, all.length - RECENT_LIMIT),
             showAll, rows: showAll ? all : all.slice(0, RECENT_LIMIT) };
  });
  const COLS = 8;

  return <>
    {/* Title, company filter, "New Borrowing Base" and its form stay pinned
        under the app's top bar (73px, also sticky) while the list scrolls. */}
    <div style={{ position: "sticky", top: 73, zIndex: 50, background: "var(--color-background-primary, #ffffff)",
                  margin: "-16px -28px 16px", padding: "12px 28px 2px",
                  borderBottom: "0.5px solid var(--color-border-tertiary)", boxShadow: "0 4px 6px -6px rgba(0,0,0,0.25)" }}>
    <SectionHeader title="Borrowing Bases" actions={[
      <select key="filter" value={filterAcct} onChange={e => setFilterAcct(e.target.value)}
        style={{ padding: "6px 12px", fontSize: 13, borderRadius: 6, border: "0.5px solid var(--color-border-secondary)", background: "var(--color-background-primary)", color: "var(--color-text-primary)" }}>
        <option value="">All companies</option>
        {accounts.map(a => <option key={a.id} value={a.id}>{a.companyname}</option>)}
      </select>,
      <Btn key="new" variant="primary" onClick={() => { setOpen(o => !o); setUerr(null); }}>{open ? "Cancel" : "＋ New Borrowing Base"}</Btn>
    ]} />

    {open && (
      <Card style={{ padding: 16, marginBottom: 20, borderLeft: "3px solid " + B.cyan }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1.6fr 2fr 1fr auto", gap: 14, alignItems: "end" }}>
          <div>
            <label style={lbl}>Funded Account</label>
            <select value={acct} onChange={e => setAcct(e.target.value)} style={inp}>
              <option value="">— Select account —</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.companyname}{a.accountnumber ? ` (${a.accountnumber})` : ""}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Loan</label>
            <select value={loanid} onChange={e => setLoanid(e.target.value)} disabled={!acct} style={{ ...inp, background: !acct ? "#f0f0f0" : "#fff" }}>
              <option value="">{!acct ? "— Pick account first —" : !acctLoans.length ? "— No revolving lines —" : acctLoans.length === 1 ? "— Select loan —" : "— Select one of " + acctLoans.length + " lines —"}</option>
              {acctLoans.map(l => <option key={l.id} value={String(l.id)}>{l.loannumber || l.id}{l.facilitytype ? ` · ${l.facilitytype}` : ""}{l.loanstatus ? ` · ${l.loanstatus}` : ""}</option>)}
            </select>
          </div>
          {/* ── FROM BOX, OR FROM THIS MACHINE ──
              Box is the default. It is the only route a large certificate can
              take — an upload is base64'd into one request and the platform
              refuses those over 30 MB — and it guarantees the workbook that gets
              parsed is the same one that gets linked to the record. */}
          <div>
            {/* The toggle lives on the LABEL line, not under the control.
                The row is aligned on its bottom edge (alignItems: "end"), so
                anything below this field's input pushes the input upward and it
                stops lining up with the account and loan dropdowns beside it.
                Putting the toggle here keeps every cell the same shape — label,
                then control — which is what makes the row read as a row. */}
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between",
                          gap: 8, marginBottom: 4 }}>
              <label style={{ ...lbl, marginBottom: 0 }}>
                Certificate
                {src === "upload" && (
                  <span style={{ textTransform: "none", fontWeight: 400 }}> (max 21 MB)</span>
                )}
              </label>
              <span onClick={() => { setSrc(s => (s === "box" ? "upload" : "box")); setUerr(null); }}
                    style={{ cursor: "pointer", color: B.cyanDark, fontWeight: 600, fontSize: 11,
                             whiteSpace: "nowrap",
                             textDecoration: "underline", textDecorationStyle: "dotted" }}>
                {src === "box" ? "Upload a file instead" : "Pick from Box instead"}
              </span>
            </div>
            {src === "box" ? (
              <select value={boxFileId} onChange={e => setBoxFileId(e.target.value)}
                      disabled={!acct || boxBusy}
                      style={{ ...inp, background: (!acct || boxBusy) ? "#f0f0f0" : "#fff" }}>
                <option value="">
                  {!acct ? "— Pick account first —"
                    : boxBusy ? "— Reading Box folder… —"
                    : boxFiles === null ? "— Reading Box folder… —"
                    : boxFiles.length === 0 ? "— Nothing found in Box —"
                    : "— Select the certificate —"}
                </option>
                {(boxFiles || []).map(f => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                    {f.size != null ? ` · ${(f.size / 1024 / 1024).toFixed(1)} MB` : ""}
                    {f.folder ? ` · ${f.folder}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input type="file" accept=".xlsx" onChange={e => setFile(e.target.files[0])} style={{ ...inp, padding: "6px 8px" }} />
            )}
          </div>
          <div>
            <label style={lbl}>As-of date <span style={{ textTransform: "none", fontWeight: 400 }}>(optional)</span></label>
            <input type="date" value={asof} onChange={e => setAsof(e.target.value)} style={inp} />
          </div>
          <Btn variant="primary"
               disabled={busy || !acct || !loanid || (src === "box" ? !boxFileId : !file)}
               onClick={upload}>
            {busy ? "Parsing…" : src === "box" ? "Load & Parse" : "Upload & Parse"}
          </Btn>
        </div>

        {/* No folder recorded, a truncated listing, or Box unreachable. All three
            mean the same thing to the user — upload it instead — so all three
            say so in the same place rather than leaving an empty dropdown to
            be interpreted. */}
        {src === "box" && boxNote && (
          <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, fontSize: 12,
                        background: "#FAEEDA", border: "0.5px solid #EBD3A0", color: "#7a5200" }}>
            {boxNote}
          </div>
        )}
        {/* Only the upload path needs this. It exists because a renamed file
            can't be found by name — but when the file is chosen FROM Box its id
            is already known, so there is nothing to override and offering the
            box would just invite a conflicting answer. */}
        {src === "upload" && (
          <div style={{ marginTop: 12 }}>
            <label style={lbl}>Box link <span style={{ textTransform: "none", fontWeight: 400 }}>(optional — only if the file was renamed)</span></label>
            <input type="text" value={boxlink} onChange={e => setBoxlink(e.target.value)} placeholder="https://app.box.com/file/…" style={inp} />
          </div>
        )}
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 8 }}>
          Leave the as-of date blank to use the certificate's own date. Re-loading the same account + as-of replaces the prior certificate.
          {src === "box"
            ? " The file is read straight from Box and linked to this record — nothing is uploaded and no copy is made."
            : " The signed file is linked automatically from this account's Box folder by file name — no copy is made."}
        </div>
        {uerr && <div style={{ color: "#993C1D", fontSize: 12, marginTop: 8 }}>{uerr}</div>}
      </Card>
    )}
    </div>

    <Card style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
        <thead><tr>
          <Th>As-of</Th>
          <ThNum>Ending A/R</ThNum>
          <ThNum>Eligible A/R</ThNum>
          <ThNum>Net Avail A/R</ThNum>
          <ThNum>Borrowing Base Avail</ThNum>
          <ThNum>Excess / (Deficit)</ThNum>
          <Th>Uploaded</Th>
          <Th>View Online</Th>
        </tr></thead>
        <tbody>
          {shown.length === 0
            ? <tr><td colSpan={COLS} style={{ padding: 20, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>{filterAcct ? "No borrowing bases for this company." : 'No borrowing bases yet. Click "New Borrowing Base" to upload one.'}</td></tr>
            : groups.map(group => [
              <tr key={"hdr-" + group.name}>
                <td colSpan={COLS} style={{ padding: "8px 12px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: B.cyanDark, background: B.cyanLight }}>
                  {group.name}<span style={{ marginLeft: 8, fontWeight: 400 }}>{group.total} borrowing base{group.total !== 1 ? "s" : ""}
                    {group.total > RECENT_LIMIT && !group.showAll ? ` · showing ${RECENT_LIMIT} most recent` : ""}</span>
                </td>
              </tr>,
              ...group.rows.map(r => {
                const excess = parseFloat(r.excess_availability);
                return (
                  <tr key={r.id} style={{ cursor: "pointer" }}
                    onClick={() => go("collateral-bbc", { bbid: r.id })}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <Td>{fmtDate(r.asofdate)}</Td>
                    <TdNum>{fmt(r.ar_ending_balance || 0)}</TdNum>
                    <TdNum>{fmt(r.ar_total_eligible || 0)}</TdNum>
                    <TdNum>{fmt(r.ar_net_available || 0)}</TdNum>
                    <TdNum>{fmt(r.borrowing_base_avail || 0)}</TdNum>
                    <TdNum style={{ color: excess < 0 ? "#993C1D" : "inherit", fontWeight: 500 }}>{fmt(r.excess_availability || 0)}</TdNum>
                    <Td style={{ color: "var(--color-text-secondary)", fontSize: 12 }}>{fmtDate(r.uploadedat)}</Td>
                    <Td>{r.box_url
                      ? <a href={r.box_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: B.cyanDark, fontWeight: 500 }}>View Online</a>
                      : <span style={{ color: "var(--color-text-secondary)" }}>—</span>}</Td>
                  </tr>
                );
              }),
              ...(group.total > RECENT_LIMIT ? [
                <tr key={"more-" + group.name}>
                  <td colSpan={COLS} style={{ padding: "6px 12px", fontSize: 12, borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
                    <span style={{ color: B.cyanDark, fontWeight: 500, cursor: "pointer" }}
                      onClick={() => setExpanded(x => ({ ...x, [group.name]: !x[group.name] }))}>
                      {group.showAll ? `Show ${RECENT_LIMIT} most recent only` : `Show ${group.hidden} older`}
                    </span>
                  </td>
                </tr>
              ] : [])
            ])}
        </tbody>
      </table>
    </Card>
  </>;
};
